import { AuthState } from "./AuthState";
import { Auth0Repository } from "./Auth0Repository";
import { AuthUIController } from "./AuthUIController";
import { LoginTrackerService } from "./LoginTrackerService";
import { RegistrationWallService } from "./services/RegistrationWallService";
import { ActionRepository } from "./repositories/ActionRepository";
import { UserService } from "./services/UserService";
import { UserStore } from "./model/UserStore";
import { logService } from "@Engage.Core";
class Auth0ClientSingleton {
    constructor() {
        this.repository = new Auth0Repository();
        this.enableAuth0JwtCookie = true;
        this.seamlessSI = true;
    }
    async init(options) {
        var _a, _b;
        this.redirectUri = options.redirectUri;
        this.enableAuth0JwtCookie = (_a = options.enableAuth0JwtCookie) !== null && _a !== void 0 ? _a : true;
        this.seamlessSI = (_b = options.seamlessSI) !== null && _b !== void 0 ? _b : true;
        this.audience = options.audience;
        // lazy load — keeps @auth0/auth0-spa-js out of the main bundle
        const { createAuth0Client } = await import(
        /* webpackChunkName: "auth0" */ "@auth0/auth0-spa-js");
        this.client = await createAuth0Client({
            domain: options.domain,
            clientId: options.clientId,
            cacheLocation: "localstorage",
            useRefreshTokens: true,
            authorizationParams: {
                redirect_uri: options.redirectUri,
                scope: "openid profile email offline_access",
                audience: this.audience
            }
        });
        await this.handleRedirect();
        await this.restoreSession();
        await this.trySeamlessLogin();
    }
    // ---------------- LOGIN ----------------
    login() {
        return this.client.loginWithRedirect({
            authorizationParams: {
                redirect_uri: this.buildRedirectUri(window.location.href)
            }
        });
    }
    register() {
        return this.client.loginWithRedirect({
            authorizationParams: {
                redirect_uri: this.buildRedirectUri(window.location.href),
                screen_hint: "signup"
            }
        });
    }
    // ---------------- LOGOUT ----------------
    async logout() {
        const returnUrl = window.location.href;
        AuthState.logout();
        this.repository.deleteAuth0AccountId();
        this.repository.deleteAuth0JwtCookie();
        this.repository.deleteUserDataExpiration();
        ActionRepository.deletePossibilityPresentForUser();
        AuthUIController.updateUI(false);
        await this.client.logout({
            logoutParams: {
                returnTo: `${this.redirectUri}?state=logout&returnUrl=${encodeURIComponent(returnUrl)}`
            }
        });
    }
    // ---------------- REDIRECT ----------------
    async handleRedirect() {
        const params = new URLSearchParams(window.location.search);
        const state = params.get("state");
        const returnUrl = params.get("returnUrl");
        if (state === "logout") {
            this.cleanupUrl();
            window.location.replace(returnUrl || "/");
            return;
        }
        if (!params.has("code"))
            return;
        try {
            await this.client.handleRedirectCallback();
            await this.setAuthCookies();
            AuthState.login();
            const loginCount = await LoginTrackerService.getLoginCount();
            if (loginCount !== null && loginCount <= 1) {
                RegistrationWallService.handleFirstLogin();
                logService.log("First login detected → registration flow");
            }
            else {
                logService.log("Existing user login");
            }
            AuthUIController.updateUI(true);
        }
        catch (err) {
            logService.error("Auth0 redirect error:", err);
        }
        this.cleanupUrl();
        window.location.replace(returnUrl || "/");
    }
    // ---------------- SESSION RESTORE ----------------
    async restoreSession() {
        try {
            const isAuthenticated = await this.client.isAuthenticated();
            if (!isAuthenticated)
                return;
            await this.setAuthCookies();
            AuthState.login();
            AuthUIController.updateUI(true);
        }
        catch (_a) { }
    }
    // ---------------- SEAMLESS LOGIN ----------------
    async trySeamlessLogin() {
        if (!this.seamlessSI)
            return;
        if (this.repository.getAuth0AccountId())
            return;
        try {
            await this.client.getTokenSilently({
                cacheMode: "off",
                authorizationParams: {
                    audience: this.audience,
                    scope: "openid profile email offline_access"
                }
            });
            await this.setAuthCookies();
            AuthState.login();
            AuthUIController.updateUI(true);
        }
        catch (_a) { }
    }
    // ---------------- COOKIES ----------------
    async setAuthCookies() {
        const user = await this.client.getUser();
        if (user === null || user === void 0 ? void 0 : user.sub) {
            this.repository.setAuth0AccountId(user.sub, 1);
            // Only call the user data API if data is stale or missing.
            // Mirrors ConneXt v2 needRefreshUserData() + Mg2Account.hasIsActualData().
            if (this.needsUserDataRefresh(user.sub)) {
                await UserService.syncUser(user.sub, true);
                // Mark data as fresh for 1 day (mirrors Mg2Account.setActualDataOnDay())
                this.repository.setUserDataExpiration(1);
            }
        }
        if (!this.enableAuth0JwtCookie)
            return;
        const claims = await this.client.getIdTokenClaims();
        if ((claims === null || claims === void 0 ? void 0 : claims.__raw) && claims.exp) {
            this.repository.setAuth0JwtCookie(claims.__raw, claims.exp * 1000);
        }
    }
    needsUserDataRefresh(userId) {
        // 1. No expiration cookie → data is stale
        if (!this.repository.getUserDataExpiration())
            return true;
        // 2. Cached user ID doesn't match current Auth0 user
        const cached = UserStore.getUser();
        if (!cached || cached.CustomerRegistrationId !== userId)
            return true;
        // 3. Cached data has no MasterId (invalid)
        if (!cached.MasterId)
            return true;
        return false;
    }
    buildRedirectUri(returnUrl) {
        const url = new URL(this.redirectUri, window.location.origin);
        url.searchParams.set("returnUrl", returnUrl);
        return url.toString();
    }
    cleanupUrl() {
        window.history.replaceState({}, document.title, this.redirectUri);
    }
    // ---------------- PUBLIC API ----------------
    isReady() {
        return !!this.client;
    }
    getUser() {
        return this.client.getUser();
    }
    isAuthenticated() {
        return this.client.isAuthenticated();
    }
    getToken(forceRefresh = false) {
        return this.client.getTokenSilently({
            cacheMode: forceRefresh ? "off" : "on",
            authorizationParams: {
                audience: this.audience,
                scope: "openid profile email offline_access"
            }
        });
    }
}
export const Auth0ClientInstance = new Auth0ClientSingleton();
